import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import Joi from "joi-browser";
import moment from "moment";
import Input from "./Input";
import Select from "./Select";
import {
  getAvailableFosterBeds,
  getClientDiagnosisDetails,
  getClientPrograms,
  getProgramServiceItemGroup,
  getProgramServiceItems,
  getServiceItemStatuses,
  searchContact,
} from "../services/soar";
import {
  getFieldDate,
  getFieldName,
  getFieldText,
  updateEntity,
} from "../../scripts/helperScripts";

function BedSearchModal({
  isOpen,
  hideModal,
  nextModal,
  isLastModal,
  clientId,
  clientProgram,
  updateBeds,
}) {
  const clientProgramAdmitDate = new Date(
    clientProgram && getFieldDate(clientProgram.ccx_admitdate)
  );
  const [data, setData] = useState({});
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [clientProgramOptions, setClientProgramOptions] = useState([]);
  const [fosterHomeBedOptions, setFosterHomeBedOptions] = useState([]);
  const [fosterHomeBedsLoading, setFosterHomeBedsLoading] = useState(false);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [serviceItemsLoading, setServiceItemsLoading] = useState(false);
  const [serviceStatusOptions, setServiceStatusOptions] = useState([]);
  const [clientDiagnosisOptions, setClientDiagnosisOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    client: Joi.string().allow("").label("Client"),
    clientProgram: Joi.string().required().label("Client Program"),
    // bedType: Joi.string().required().label("Bed Type"),
    fosterHomeBed: Joi.string().required().label("Foster Home Bed"),
    serviceItem: Joi.string().required().label("Service Item"),
    serviceStatus: Joi.string().required().label("Service Status"),
    clientDiagnosis: Joi.string().required().label("Client Diagnosis"),
    dateOfAdmission: Joi.alternatives([
      Joi.date()
        .required()
        .min(moment(clientProgramAdmitDate).subtract(1, "day").toDate())
        .max(moment().add(5, "days").toDate())
        .label("Date of Admission")
        .error((errors) => {
          errors.forEach((error) => {
            switch (error.type) {
              case "date.max":
                error.message = `Date of Admission cannot be future dated beyond ${moment(
                  error.context.limit
                ).format("L")}`;
                break;
              case "date.min":
                error.message = `Date of Admission cannot be before ${moment(
                  error.context.limit
                )
                  .add(1, "d")
                  .format("L")}`;
                break;
              default:
                return error.message;
            }
          });
          return errors;
        }),
    ]),
  };

  useEffect(() => {
    if (!safyInfo) navigate("/");
  });

  useEffect(() => {
    return () => (isMountedRef.current = false);
  }, []);

  useEffect(() => {
    if (data.serviceItem) {
      getServiceItemStatuses(data.serviceItem).then((response) => {
        if (response && isMountedRef.current) {
          console.log("ServiceStatuses: ", response);
          const admissionStatuses = response
            .sort((a, b) =>
              a.ccx_status > b.ccx_status
                ? 1
                : b.ccx_status > a.ccx_status
                ? -1
                : 0
            )
            .map((status) => {
              return {
                _id: status.ccx_servicestatusid,
                name: status.ccx_status,
              };
            });

          setServiceStatusOptions(admissionStatuses);
        }
      });
    }
  }, [data.serviceItem]);

  useEffect(() => {
    if (clientId && isOpen) {
      const obj = { ...data };
      obj.client = clientId;
      setData(obj);
    }
  }, [clientId, isOpen]);

  useEffect(() => {
    if (isOpen && data.client !== undefined && data.client !== "") {
      getClientPrograms(data.client).then((response) => {
        if (response && isMountedRef.current) {
          const options = response
            .filter(
              (program) =>
                program.ccx_dischargedate === undefined &&
                program.ccx_name.includes("Foster Home")
            )
            .map((program) => {
              return {
                _id: program.ccx_client_programid,
                name: program.ccx_name,
                programUnitId: getFieldText(program.ccx_programunit),
              };
            });

          setClientProgramOptions(options);

          if (options.length === 1) {
            const obj = { ...data };
            obj.clientProgram = options[0]._id;
            setData(obj);
          }
        }
      });
    }
  }, [data.client, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      data.client !== undefined &&
      data.client !== "" &&
      data.clientProgram !== undefined &&
      data.clientProgram !== ""
    ) {
      getClientDiagnosisDetails(data.client).then((response) => {
        if (response && isMountedRef.current) {
          const options = response
            .filter(
              (dxDetails) =>
                dxDetails.ccx_expdate === undefined &&
                dxDetails["dx.ccx_expdate"] === undefined &&
                getFieldName(dxDetails.ccx_clientdiagnosis) ===
                  "Bed Diagnosis" &&
                getFieldText(dxDetails.ccx_clientprogram) === data.clientProgram
            )
            .map((dxDetails) => {
              return {
                _id: getFieldText(dxDetails.ccx_clientdiagnosis),
                name: getFieldName(dxDetails.ccx_clientdiagnosis),
              };
            });

          setClientDiagnosisOptions(options);
          if (options[0]) {
            const obj = { ...data };
            obj.clientDiagnosis = options[0]._id;
            setData(obj);
          }
        }
      });
    }
  }, [data.client, data.clientProgram, isOpen]);

  useEffect(() => {
    if (
      isOpen &&
      data.clientProgram !== undefined &&
      data.clientProgram !== ""
    ) {
      const clientProgramOption = clientProgramOptions.filter(
        (option) => option._id === data.clientProgram
      )[0];
      if (clientProgramOption) {
        setServiceItemsLoading(true);
        getProgramServiceItemGroup(
          clientProgramOption.programUnitId,
          safyInfo.user.safyInfo.BusinessUnitIdName
        ).then((response) => {
          if (response && isMountedRef.current)
            getProgramServiceItems(
              response[0].ccx_cost_center_categoryid,
              safyInfo.user.safyInfo.BusinessUnitIdName
            ).then((response2) => {
              if (isMountedRef.current) {
                setServiceItemsLoading(false);
                if (response2) {
                  const options = response2.map((serviceItem) => {
                    return {
                      _id: serviceItem.ccx_service_itemid,
                      name: serviceItem.ccx_name,
                    };
                  });

                  setServiceItemOptions(options);
                }
              }
            });
        });

        if (data.dateOfAdmission !== undefined && data.dateOfAdmission !== "") {
          const obj = { ...data };
          obj.fosterHomeBed = "";
          setData(obj);
          setFosterHomeBedsLoading(true);
          getAvailableFosterBeds(
            data.dateOfAdmission,
            clientProgramOption.programUnitId,
            safyInfo.user.safyInfo.BusinessUnitIdName
          ).then((response) => {
            if (isMountedRef.current) {
              setFosterHomeBedsLoading(false);
              if (response) {
                console.log("AvailableFosterBeds: ", response);
                const options = response.map((bed) => {
                  return {
                    _id: bed.ccx_fosterchildid,
                    name: bed.ccx_name,
                    homeName: getFieldName(bed.ccx_fostercarehome),
                    homeId: getFieldText(bed.ccx_fostercarehome),
                  };
                });

                setFosterHomeBedOptions(options);
              }
            }
          });
        }
      }
    }
  }, [data.clientProgram, data.dateOfAdmission, isOpen]);

  const handleClientSearch = async () => {
    const response = await searchContact(
      clientSearchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current && !response) {
      return;
    }

    const options = response.map((contact) => {
      return {
        _id: contact.contactid,
        name: `${contact.fullname}${
          contact.birthdate ? " - DOB: " + getFieldDate(contact.birthdate) : ""
        }`,
      };
    });
    setClientOptions(options);
  };

  const handleChange = ({ currentTarget: input }) => {
    const errs = { ...errors };
    const errorMessage = validateProperty(input);

    if (errorMessage) {
      errs[input.name] = errorMessage;
    } else {
      delete errs[input.name];
    }
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);

    setErrors(errs);
  };

  const validateProperty = ({ name, value }) => {
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema);
    return error ? error.details[0].message : null;
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(data, joiSchema, options);
    if (!error) return null;

    let validationErrors = {};
    for (let item of error.details)
      validationErrors[item.path[0]] = item.message;
    setErrors(validationErrors);
    return validationErrors;
  };

  const handleSave = async () => {
    const fosterHomeBed = fosterHomeBedOptions.filter(
      (option) => option._id === data.fosterHomeBed
    )[0];

    const obj = {
      ccx_contact: {
        type: "entity",
        value: {
          entityName: "contact",
          entityId: data.client,
        },
      },
      ccx_clientprogram: {
        type: "entity",
        value: {
          entityName: "ccx_client_program",
          entityId: data.clientProgram,
        },
      },
      ccx_clientdiagnosis: {
        type: "entity",
        value: {
          entityName: "ccx_clientdiagnosis",
          entityId: data.clientDiagnosis,
        },
      },
      ccx_fostercarehome: {
        type: "entity",
        value: {
          entityName: "ccx_fostercarehome",
          entityId: fosterHomeBed.homeId,
        },
      },
      ccx_serviceitem: {
        type: "entity",
        value: {
          entityName: "ccx_service_item",
          entityId: data.serviceItem,
        },
      },
      ccx_servicestatuslookup: {
        type: "entity",
        value: {
          entityName: "ccx_servicestatus",
          entityId: data.serviceStatus,
        },
      },
      ccx_effectivedate: {
        type: "datetime",
        value: new Date(data.dateOfAdmission),
      },
      ccx_actualadmission: {
        type: "datetime",
        value: new Date(data.dateOfAdmission),
      },
    };

    const toastId = toast.loading("Foster Home Bed is being created...", {
      type: toast.TYPE.INFO,
    });
    updateEntity(
      "ccx_fosterchild",
      data.fosterHomeBed,
      JSON.stringify(obj),
      safyInfo
    )
      .then(() => {
        toast.update(toastId, {
          render: "Foster Home Bed has been created.",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
        updateBeds && updateBeds(true);
      })
      .catch(() => {
        toast.update(toastId, {
          render: "An error occurred while creating the Foster Home Bed.",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
          closeOnClick: true,
        });
      });
  };

  const handleSaveAndAdd = () => {
    handleSave();
    resetForm();
  };

  const resetForm = () => {
    setData({});
    setClientOptions([]);
    setFosterHomeBedOptions([]);
    setClientProgramOptions([]);
    setClientDiagnosisOptions([]);
    setServiceItemOptions([]);
    setErrors({});
  };

  const handleHideModal = () => {
    if (hideModal) {
      resetForm();
      hideModal();
    }
  };
  return (
    <>
      {isOpen ? (
        <Modal show={isOpen} animation={false}>
          <Modal.Header>
            <Modal.Title>Bed Search</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {(clientId === undefined || clientId === null) && (
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Input
                      name="clientSearchQuery"
                      label="Search for Client"
                      placeholder="Search for Client..."
                      onChange={(e) =>
                        setClientSearchQuery(e.currentTarget.value)
                      }
                    />
                  </div>
                  <div className="col-3">
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: "30px" }}
                      onClick={handleClientSearch}
                      disabled={clientSearchQuery === ""}
                    >
                      Search
                    </button>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Select
                      name="client"
                      label="Clients"
                      options={clientOptions}
                      onChange={handleChange}
                      error={errors.client}
                    />
                  </div>
                </div>
              </div>
            )}
            {clientProgram === undefined ? (
              <div>Client Program is being created...</div>
            ) : null}
            <div
              className={
                data.dateOfAdmission === undefined ||
                data.dateOfAdmission === ""
                  ? "text-danger"
                  : ""
              }
            >
              <Input
                name="dateOfAdmission"
                label="Date of Admission"
                type="date"
                value={data.dateOfAdmission}
                onChange={handleChange}
                error={errors.dateOfAdmission}
                disabled={clientProgram === undefined}
              />
            </div>
            {/* <RadioButton
          name="bedType"
          label="Bed Type:"
          value={data.bedType}
          options={[
            { _id: "FosterHomeBed", name: "Foster Care Home" },
            { _id: "BedAppoinment", name: "Bed Appointment" },
          ]}
          onChange={handleChange}
          error={errors.bedType}
        /> */}
            <Select
              name="clientProgram"
              label="Client Program"
              value={data.clientProgram}
              options={clientProgramOptions}
              onChange={handleChange}
              error={errors.clientProgram}
            />
            <Select
              name="fosterHomeBed"
              label="Foster Home Bed"
              options={fosterHomeBedOptions}
              onChange={handleChange}
              disabled={
                data.dateOfAdmission === undefined ||
                data.dateOfAdmission === "" ||
                fosterHomeBedsLoading
              }
              error={errors.fosterHomeBed}
            />
            <Select
              name="serviceItem"
              label="Service Item"
              options={serviceItemOptions}
              onChange={handleChange}
              error={errors.serviceItem}
              disabled={serviceItemsLoading}
            />
            <Select
              name="serviceStatus"
              label="Service Status"
              options={serviceStatusOptions}
              onChange={handleChange}
              error={errors.serviceStatus}
            />
            <Select
              name="clientDiagnosis"
              label="Client Diagnosis"
              value={data.clientDiagnosis}
              options={clientDiagnosisOptions}
              onChange={handleChange}
              error={errors.clientDiagnosis}
            />
          </Modal.Body>
          <Modal.Footer>
            {nextModal === undefined && (
              <>
                <button className="btn btn-danger" onClick={handleHideModal}>
                  Cancel
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) {
                      handleSave();
                      handleHideModal();
                    }
                  }}
                >
                  Submit
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={() => {
                    if (!validate()) handleSaveAndAdd();
                  }}
                >
                  Submit and Add New
                </button>
              </>
            )}
            {nextModal && (
              <>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    resetForm();
                    nextModal();
                  }}
                >
                  Skip
                </button>
                <button
                  className={
                    Object.keys(errors).length === 0
                      ? "btn btn-primary"
                      : "btn btn-secondary"
                  }
                  disabled={Object.keys(errors).length !== 0}
                  onClick={async () => {
                    if (!validate()) {
                      await handleSave();
                      resetForm();
                      nextModal();
                    }
                  }}
                >
                  Submit and {isLastModal ? "End" : "Next"}
                </button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default BedSearchModal;
